/**
 * /it/prd — PRD concept page (Italian).
 * Links /it/roadmap-di-prodotto + /it/registro-delle-decisioni.
 */
import { MarketingShell } from './MarketingShell';

export function ItPrd() {
  return (
    <MarketingShell
      currentLocale="it"
      hreflangPath="/prd"
      hreflangLocales={['it']}
      documentTitle="PRD: cos'è il documento dei requisiti di prodotto (+ modello) · Draftlize"
      documentDescription="Cos'è un PRD, la struttura e un modello da copiare — e come tenerlo vivo perché non diventi obsoleto dopo ogni sprint."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guida · PRD</span>
          <h1 className="serif" style={{ fontSize: 'clamp(32px, 4.3vw, 54px)', lineHeight: 1.06, margin: '12px 0 24px' }}>
            Il PRD (documento dei requisiti<br />
            <em>di prodotto), spiegato.</em>
          </h1>
          <p className="lede">
            Un <strong>PRD</strong> (Product Requirements Document / documento dei requisiti di prodotto) definisce <em>cosa</em> costruire e <em>perché</em>: il problema, lo scope, i requisiti e le decisioni che li reggono, così che team e stakeholder condividano la stessa immagine. Il punto debole arriva dopo: in due o tre sprint il PRD smette di combaciare con la realtà — <em>deriva</em>. Qui la struttura, un modello e come evitare la deriva.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">La struttura di un PRD che regge.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ol style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 22 }}>
            <li><strong>Problema e obiettivi</strong> — il problema di chi, perché ora, quale risultato.</li>
            <li><strong>Scope (dentro / fuori)</strong> — ciò che si lascia fuori di proposito.</li>
            <li><strong>Requisiti</strong> — funzionali e non funzionali, prioritizzati.</li>
            <li><strong>Decisioni e compromessi</strong> — cosa si è valutato e perché si è scartato il resto.</li>
            <li><strong>Domande aperte e criteri di successo</strong>.</li>
          </ol>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            Questo modello si copia così com'è. La parte più preziosa — e che invecchia prima — è la 4: le decisioni. Un PRD è solo la punta visibile della <a href="/it/roadmap-di-prodotto" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>roadmap</a> e delle decisioni sotto.
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Il problema vero: <em>la deriva del PRD.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Un PRD è corretto il giorno in cui viene approvato. Poi una riunione cambia un'ipotesi, un edge case ribalta una decisione — e il documento resta fermo. Nessuno mantiene a mano un documento di dodici pagine. Settimane dopo, un agente IA costruisce su una spec che non regge più.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">I requisiti diventano schede</h3>
            <p>Ogni requisito e decisione diventa una scheda indirizzabile invece di una riga a pagina sette — con la motivazione e il responsabile, trovabile per ID.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Se una decisione cambia, il PRD lo segnala</h3>
            <p>Quando una decisione successiva ne ribalta una precedente, le schede dipendenti — e la parte di PRD interessata — diventano «stale» da sole. Il documento non mente in silenzio per tre settimane.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Il tuo agente IA legge la spec valida</h3>
            <p>Claude Code o Cursor leggono il PRD via MCP — attuale o segnalato esplicitamente, non un documento statico di cui nessuno conosce lo stato.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Un PRD è corretto il giorno in cui viene approvato. Il problema sono le sei settimane dopo, in cui nessuno lo aggiorna.
        </blockquote>
        <cite>Trasforma i requisiti in schede e il PRD ti avvisa da solo quando è obsoleto. Questo è Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Domande frequenti.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'PRD o specifica?', a: 'Il PRD definisce «cosa e perché», a livello superiore; la specifica entra nel «come costruirlo». Se la decisione nel PRD cambia, la spec deve cambiare, e quel collegamento è cruciale.' },
            { q: 'Chi lo scrive?', a: 'Di solito il PM, insieme a engineering e design. L\'essenziale è che le decisioni interne restino tracciabili e con il loro perché.' },
            { q: 'Quanto lungo?', a: 'Basta che siano chiari problema, scope, requisiti e decisioni. Più della lunghezza conta poter accorgersi quando qualcosa diventa obsoleto.' },
            { q: 'Come evito che diventi obsoleto?', a: 'Rendi requisiti e decisioni schede collegate, non paragrafi congelati. Se una decisione cambia, Draftlize segna «stale» ciò che dipende. Prosegui con <link>.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>
                {f.a.replace(' Prosegui con <link>.','')}
                {i === 3 && (<>{' Prosegui con '}<a href="/it/registro-delle-decisioni" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>registro delle decisioni</a>{'.'}</>)}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Un PRD che non deriva <em>— gratis con $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>Registrati e ricevi $5</span>
          <span>Paghi solo ciò che usi</span>
          <span>Il saldo non scade mai</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Scrivi il PRD come schede collegate e ogni requisito diventa indirizzabile. Se una decisione cambia, segna «stale» ciò che è coinvolto — fine della deriva del PRD.
          </p>
          <a className="btn-primary" href="/">Inizia gratis con $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
